import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useApiRequest } from '../../../api/useApiRequest';
import { IfUserHasPermission } from '../../../authorisation/IfUserHasPermission';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { ButtonRow, PrimaryButton } from '../../../shared/Button';
import { Header1 } from '../../../shared/Headers';
import { spacing05 } from '../../../styling/spacing';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { DataForDesignDrawingsResponse } from './dataForDesignDrawingsResponse';
import { GenerateBillOfMaterialsForDesignModal } from '../GenerateBillOfMaterialsForDesignModal';
import { UnitDataForDesignDrawings } from './UnitDataForDesignDrawings';
import { GetRequest } from '../../../shared/GetRequest';

type Props = { project: Project } & RouteComponentProps<{ projectId: string; designId: string }>;

export const DataForDesignDrawings = ({ project, designId }: Props) => {
  const getDesignRequest = useApiRequest<DataForDesignDrawingsResponse>({
    endpointUrl: `projects/${project.projectId}/designs/${designId}/data-for-design-drawings`,
    httpMethod: 'get',
  });

  const [isGenerateBillOfMaterialsModalOpen, setIsGenerateBillOfMaterialsModalOpen] = useState(false);

  return (
    <GetRequest getDataRequest={getDesignRequest}>
      {({
        response: {
          designId: responseDesignId,
          designName,
          externalAirTemperatureInDegreesCelsius,
          units,
          unitPumps,
          manifoldPumps,
          pipes,
          heatSources,
          hasBillOfMaterials,
        },
      }) => (
        <>
          <GenerateBillOfMaterialsForDesignModal
            projectId={project.projectId}
            designId={responseDesignId}
            isOpen={isGenerateBillOfMaterialsModalOpen}
            closeModal={() => setIsGenerateBillOfMaterialsModalOpen(false)}
            shouldRegenerate={hasBillOfMaterials}
          />
          <BreadcrumbBar>
            <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
            <BreadcrumbSeparator />
            <BreadcrumbLink to={projectUrls.projectDetails(project.projectId)}>
              {project.projectReference}
            </BreadcrumbLink>
            <BreadcrumbSeparator />
            <div>{designName}</div>
            <BreadcrumbSeparator />
            <div>Data for Design Drawings</div>
          </BreadcrumbBar>
          <ProjectHeader project={project} />
          <PageContainer>
            <Header1>Data for Design Drawings</Header1>
            {units.map(unit => (
              <UnitDataForDesignDrawings
                key={unit.unitId}
                projectId={project.projectId}
                designId={responseDesignId}
                unit={unit}
                externalAirTemperatureInDegreesCelsius={externalAirTemperatureInDegreesCelsius}
                unitPumps={unitPumps}
                manifoldPumps={manifoldPumps}
                pipes={pipes}
                heatSources={heatSources}
              />
            ))}
            <IfUserHasPermission requiredPermission="Edit">
              <PageActionsRow $align="left">
                <PrimaryButton onClick={() => setIsGenerateBillOfMaterialsModalOpen(true)}>
                  {hasBillOfMaterials ? 'Regenerate BOM' : 'Generate BOM'}
                </PrimaryButton>
              </PageActionsRow>
            </IfUserHasPermission>
          </PageContainer>
        </>
      )}
    </GetRequest>
  );
};

const PageActionsRow = styled(ButtonRow)`
  margin-bottom: ${spacing05};
`;
